import ExcelJS, { CellValue, Workbook } from "exceljs";
import * as XLSX from "xlsx";

import censusTemplateUrl from "../assets/census_template.xlsx?url";
import {
  ColumnName,
  Coverage,
  FormCommonProps,
  FormState,
  ProcessedRow,
} from "../types";

import {
  COVERED_COVERAGES,
  IDLE,
  TEMPLATE_NAME,
  TERM_DISABILITY_COVERAGES,
} from "./constants";

const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0 || isNaN(bytes)) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

function isValidNumber(str: string): [boolean, number] {
  if (str === "") {
    return [false, NaN];
  }
  const num = Number(str);
  return [Number.isFinite(num), num];
}

function isValidDate(str: string): [true, string] | [false, null] {
  const errorResponse: [false, null] = [false, null];
  if (!/\d{1,4}[/-]\d{1,2}[/-]\d{1,4}$/.test(str)) {
    return errorResponse;
  }
  const tokens = str.split("/");
  const token0 = isValidNumber(tokens[0]);
  const token1 = isValidNumber(tokens[1]);
  const token2 = isValidNumber(tokens[2]);
  if (!token0[0] || !token1[0] || !token2[0]) {
    return errorResponse;
  }

  let day = NaN,
    month = NaN,
    year = NaN;
  // if year is at the beginning
  if (tokens[0].length === 4) {
    year = token0[1];
    month = token1[1];
    day = token2[1];
  } else {
    year = token2[1];
    month = token0[1];
    day = token1[1];
  }
  if (
    !(
      Number.isInteger(year) &&
      Number.isInteger(month) &&
      Number.isInteger(day)
    )
  ) {
    return errorResponse;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return errorResponse;
  }

  // Create a Date object
  const date = new Date(year, month - 1, day);
  // Check if the date is valid

  // if the format is 'M/D/YY' it is necessary to add 1900 for final validation
  if (year < 100) {
    year = year + 1900;
  }

  if (
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day
  ) {
    return errorResponse;
  }
  return [true, `${year}-${padZeroes(month)}-${padZeroes(day)}`];
}

function padZeroes(str: string | number): string {
  return String(str).padStart(2, "0");
}

async function downloadExcelFile(
  fileName: string,
  workbook: Workbook,
): Promise<void> {
  const blob = await workbook.xlsx.writeBuffer();

  const url = URL.createObjectURL(new Blob([blob]));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadTemplate(): void {
  const link = document.createElement("a");
  link.href = censusTemplateUrl;
  link.download = TEMPLATE_NAME;

  document.body.appendChild(link);
  link.click();
  link.remove();
}

function isCoveredCoverage(c: Coverage): c is Coverage & { covered: string } {
  return COVERED_COVERAGES.includes(c.coverage);
}

function isTermDisabilityCoverage(
  c: Coverage,
): c is Coverage & { covered: string } {
  return TERM_DISABILITY_COVERAGES.includes(c.coverage);
}

function capitalizeFirstLetter(string: string): string {
  return string[0].toUpperCase() + string.slice(1);
}

function getFormInitState(init?: FormCommonProps["init"]): FormState {
  return {
    state: IDLE,
    row:
      typeof init?.row === "number" ||
      (typeof init?.row === "string" && /^\d+$/.test(init.row))
        ? `${init.row}`
        : "",
    selectedCoverages: init?.selectedCoverages ?? {},
    censusFile: null,
    censusError: null,
    fileError: null,
    processedData: [],
    fullTimeEmployees: init?.fullTimeEmployees ?? "",
    censusGroupClasses: init?.censusGroupClasses ?? [],
    // New fields for census-eliminate-redundancy feature
    coveragesColumns: [],
    classNamesDiffer: "",
  };
}

async function getWorkbookFromFile(file: File): Promise<ExcelJS.Workbook> {
  const workbook = new ExcelJS.Workbook();
  const arrayBuffer = await file.arrayBuffer();
  if (file.type === "application/vnd.ms-excel") {
    const data = new Uint8Array(arrayBuffer);
    const workbookXLSX = XLSX.read(data, { type: "array", cellNF: true });
    const arrayBufferXLSX = XLSX.write(workbookXLSX, {
      bookType: "xlsx",
      type: "array",
    });
    await workbook.xlsx.load(arrayBufferXLSX);
  } else {
    await workbook.xlsx.load(arrayBuffer);
  }
  return workbook;
}

const isDuplicate = (
  array: string[],
  value: string,
  index: number,
): boolean => {
  if (value.trim() === "") return false;
  return (
    array.filter((name, idx) => name === value && idx !== index).length > 0
  );
};

async function buildStandardizedFile(
  censusFile: File,
  processedData: ProcessedRow[],
): Promise<ExcelJS.Workbook> {
  const HEADERS_ORDER: ColumnName[] = [
    "relationship",
    "gender",
    "date of birth",
    "zip",
    "employment status",
    "employment type",
    "occupation",
    "eligibility class",
    "annual salary",
    "dental plan coverage",
    "dental plan name",
    "vision plan coverage",
    "vision plan name",
    "std election",
    "ltd election",
    "basic life/ad&d ee coverage",
    "vol life ee coverage",
    "vol ad&d ee coverage",
  ];

  const workbook = await getWorkbookFromFile(censusFile);
  const originalWorksheet = workbook.worksheets[0];
  if (originalWorksheet) {
    originalWorksheet.name = "Original Census";
  }
  const standardizedWorksheet = workbook.addWorksheet("Standardized Census");

  // Add orderNo to worksheets to keep the order when downloading
  (originalWorksheet as any).orderNo = 1;
  (standardizedWorksheet as any).orderNo = 0;

  const headers = Object.keys(processedData[0]) as ColumnName[];
  const orderedHeaders = HEADERS_ORDER.filter((header) =>
    headers.includes(header),
  );
  standardizedWorksheet.addRow(orderedHeaders);

  const preparedData = processedData.reduce(
    (mainAcc: CellValue[][], refactoredItem: ProcessedRow) => {
      const elems = orderedHeaders.reduce(
        (acc: CellValue[], head: ColumnName) => {
          acc.push(refactoredItem[head]?.processed);
          return acc;
        },
        [],
      );
      mainAcc.push(elems);
      return mainAcc;
    },
    [],
  );

  standardizedWorksheet.addRows(preparedData);
  return workbook;
}

export {
  buildStandardizedFile,
  capitalizeFirstLetter,
  downloadExcelFile,
  downloadTemplate,
  formatBytes,
  getFormInitState,
  getWorkbookFromFile,
  isCoveredCoverage,
  isDuplicate,
  isTermDisabilityCoverage,
  isValidDate,
  isValidNumber,
};
